import { Badge } from '@workspace/ui/components/badge';
import { Button } from '@workspace/ui/components/button';
import type { ColumnDef } from '@workspace/ui/components/data-table';
import { DataTable } from '@workspace/ui/components/data-table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@workspace/ui/components/dialog';
import { toast } from '@workspace/ui/components/sonner';
import dayjs from 'dayjs';
import { Copy, Loader2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { m } from '~/paraglide/messages.js';
import type { ApiKey, ApiKeyStatus } from '../-api/types';
import { findScopeEntry } from '../-config/scope-catalog';
import { useApiKeys, useRevokeApiKey } from '../-hooks/use-api-keys';

function StatusBadge({ status }: { status: ApiKeyStatus }) {
  const variant =
    status === 'active'
      ? 'default'
      : status === 'revoked'
        ? 'destructive'
        : 'secondary';

  const label =
    status === 'active'
      ? m['common.status.active']()
      : status === 'revoked'
        ? m['developer.api_keys.table.status_revoked']()
        : m['developer.api_keys.table.status_expired']();

  return <Badge variant={variant}>{label}</Badge>;
}

function RevokeDialog({ apiKey }: { apiKey: ApiKey }) {
  const [open, setOpen] = useState(false);
  const revokeMutation = useRevokeApiKey();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 text-destructive hover:text-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
          {m['developer.api_keys.table.actions.revoke_key']()}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {m['developer.api_keys.table.actions.revoke_key']()}
          </DialogTitle>
          <DialogDescription>
            {m['developer.api_keys.table.actions.confirm_revoke']()}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {m['common.cancel']()}
          </Button>
          <Button
            variant="destructive"
            disabled={revokeMutation.isPending}
            onClick={async () => {
              await revokeMutation.mutateAsync(apiKey.id);
              setOpen(false);
            }}
          >
            {revokeMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {m['developer.api_keys.table.actions.revoke_key']()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ApiKeysTable() {
  const { data, isLoading, error } = useApiKeys();

  const columns: ColumnDef<ApiKey>[] = [
    {
      accessorKey: 'name',
      header: m['developer.api_keys.table.columns.name'](),
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue('name')}</div>
      ),
    },
    {
      accessorKey: 'key_id',
      header: m['developer.api_keys.table.columns.key_id'](),
      cell: ({ row }) => {
        const keyId = row.original.key_id;
        if (!keyId) return <span className="text-muted-foreground">-</span>;
        return (
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-xs text-muted-foreground">
              {keyId.slice(0, 20)}...
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => {
                navigator.clipboard.writeText(keyId);
                toast.success(m['common.copied_to_clipboard']());
              }}
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        );
      },
    },
    {
      accessorKey: 'domains',
      header: m['developer.api_keys.table.columns.domains'](),
      cell: ({ row }) => {
        const domains = row.original.domains;
        if (!domains || domains.length === 0) {
          return <span className="text-sm text-muted-foreground">-</span>;
        }
        if (domains.length === 1) {
          return (
            <span className="text-sm text-muted-foreground">{domains[0]}</span>
          );
        }
        return (
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8">
                {m['developer.api_keys.table.columns.domains_count']({
                  count: domains.length,
                })}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {m['developer.api_keys.table.domains_title']()}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-2">
                {domains.map((domain) => (
                  <div
                    key={domain}
                    className="rounded-md bg-muted p-2 text-sm break-all"
                  >
                    {domain}
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        );
      },
    },
    {
      accessorKey: 'scopes',
      header: m['developer.api_keys.table.columns.scopes'](),
      cell: ({ row }) => {
        const scopes = row.original.scopes;
        if (!scopes || scopes.length === 0) {
          return <span className="text-sm text-muted-foreground">-</span>;
        }
        return (
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8">
                <Badge variant="secondary" className="text-xs cursor-pointer">
                  {scopes.length} scopes
                </Badge>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {m['developer.api_keys.table.columns.scopes']()} —{' '}
                  {row.original.name}
                </DialogTitle>
                <DialogDescription>
                  {scopes.length} permissions attached to this key.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5">
                {scopes.map((scope) => {
                  const entry = findScopeEntry(scope);
                  return (
                    <div
                      key={scope}
                      className="flex items-baseline justify-between gap-3 rounded-md bg-muted/50 px-3 py-2"
                    >
                      <span className="text-sm text-foreground">
                        {entry?.label ?? scope}
                      </span>
                      <code className="shrink-0 font-mono text-[11px] text-muted-foreground">
                        {scope}
                      </code>
                    </div>
                  );
                })}
              </div>
            </DialogContent>
          </Dialog>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: m['developer.api_keys.table.columns.created'](),
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {dayjs(row.getValue('created_at')).format('MMM D, YYYY')}
        </span>
      ),
    },
    {
      accessorKey: 'expiration',
      header: m['developer.api_keys.table.columns.expiration'](),
      cell: ({ row }) => {
        const expiration = row.original.expiration;
        if (!expiration || expiration === 'never')
          return <span className="text-sm text-muted-foreground">Never</span>;

        const expDate = dayjs.unix(Number.parseInt(expiration, 10));
        return (
          <span className="text-sm text-muted-foreground">
            {expDate.format('MMM D, YYYY')}
          </span>
        );
      },
    },
    {
      accessorKey: 'status',
      header: m['developer.api_keys.table.columns.status'](),
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        if (row.original.status !== 'active') return null;
        return <RevokeDialog apiKey={row.original} />;
      },
    },
  ];

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <p className="font-medium text-destructive">{m['common.error']()}</p>
          <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <DataTable
        columns={columns}
        data={data || []}
        emptyMessage={m['developer.api_keys.table.empty_message']()}
        rowsPerPageText={m['common.table.rows_per_page']()}
        paginationText={({ pageIndex, pageCount }) =>
          m['common.table.page_x_of_y']({
            current: pageIndex + 1,
            total: pageCount,
          })
        }
      />
    </div>
  );
}
